import { ChevronDown, ChevronRight, Snowflake } from "lucide-react";
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import type { HolandaAlert, HolandaTicketBrief } from "@/api/types";
import {
  fmtDur,
  groupAgencies,
  isAlert,
  Legend,
  MiniCell,
  parcialLabel,
  STATE_META,
  TicketActions,
} from "@/components/holanda-shared";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useHolandaOverview } from "@/hooks/useHolanda";

// El monitor actualiza channel_state cada ~5 min. Si pasaron > STALE_MIN sin
// dato fresco, probablemente está caído y el tablero estaría "mintiendo verde".
const STALE_MIN = 15;

// Prioridad para ordenar la lista (lo más urgente primero) — espeja la API.
const SEVERITY: Record<string, number> = {
  ALARMA: 0,
  NOTIF_GUARDIA: 1,
  WARNING: 2,
  VIGILANDO: 3,
  POSIBLE_RUTA: 4,
};

// "Sin atender" = nadie tomó el ticket todavía (no existe o sigue abierto).
function isUnattended(ticket: HolandaTicketBrief | null): boolean {
  return !ticket || ticket.status === "abierto";
}

// Estado de atención mostrado en la columna "Atención".
function attentionMeta(ticket: HolandaTicketBrief | null): {
  label: string;
  tone: string;
} {
  if (!ticket || ticket.status === "abierto") {
    return { label: "Sin atender", tone: "font-medium text-red-600" };
  }
  if (ticket.status === "en_atencion") {
    return {
      label: ticket.attended_by
        ? `En atención: ${ticket.attended_by}`
        : "En atención",
      tone: "text-amber-600",
    };
  }
  return { label: ticket.status, tone: "text-muted-foreground" };
}

function Kpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className={`text-2xl font-semibold ${accent ?? ""}`}>{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
      </CardContent>
    </Card>
  );
}

// Lista compacta tipo hoja de cálculo: una fila por alerta (todas visibles,
// ordenadas por prioridad y luego por sin-atender primero). Clic en la fila para
// expandir los detalles y las acciones del ticket — así no ocupan espacio.
function AlertsTable({ alerts }: { alerts: HolandaAlert[] }) {
  const [openKey, setOpenKey] = useState<string | null>(null);

  const sorted = [...alerts].sort((a, b) => {
    const sev = (SEVERITY[a.state] ?? 9) - (SEVERITY[b.state] ?? 9);
    if (sev !== 0) return sev;
    const att = Number(isUnattended(a.ticket)) - Number(isUnattended(b.ticket));
    if (att !== 0) return -att; // sin atender primero
    return b.minutes_in_state - a.minutes_in_state;
  });

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="w-6 px-2 py-2" />
            <th className="px-2 py-2">Estado</th>
            <th className="px-2 py-2">Agencia / Tablero</th>
            <th className="px-2 py-2 text-center">Canal</th>
            <th className="px-2 py-2 text-center">Carritos</th>
            <th className="px-2 py-2">Apagado hace</th>
            <th className="px-2 py-2 text-right">Watts</th>
            <th className="px-2 py-2">Atención</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((a) => {
            const key = `${a.device_gid}-${a.channel_num}`;
            const open = openKey === key;
            const meta = STATE_META[a.state];
            const att = attentionMeta(a.ticket);
            return (
              <Fragment key={key}>
                <tr
                  onClick={() => setOpenKey(open ? null : key)}
                  className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                >
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {open ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge className={`whitespace-nowrap ${meta.badge}`}>
                      {meta.label}
                    </Badge>
                    {a.parcial && (
                      <div className="mt-0.5 whitespace-nowrap text-[11px] font-medium text-green-700">
                        {parcialLabel(a)}
                      </div>
                    )}
                  </td>
                  <td
                    className="max-w-[16rem] truncate px-2 py-1.5 font-medium"
                    title={a.site_name}
                  >
                    {a.site_name}
                  </td>
                  <td className="px-2 py-1.5 text-center tabular-nums">
                    {a.channel_num}
                  </td>
                  <td className="px-2 py-1.5 text-center tabular-nums">
                    {a.parcial ? "1 de 2" : a.cart_count}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {fmtDur(a.minutes_in_state)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {Math.round(a.watts)} W
                  </td>
                  <td
                    className={`whitespace-nowrap px-2 py-1.5 text-xs ${att.tone}`}
                  >
                    {att.label}
                    {a.ticket && a.ticket.notes_count > 0 && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {a.ticket.notes_count} nota(s)
                      </span>
                    )}
                  </td>
                </tr>
                {open && (
                  <tr className="border-b bg-muted/20 last:border-0">
                    <td colSpan={8} className="px-4 py-3">
                      {meta.desc && (
                        <p className="mb-2 text-xs text-muted-foreground">
                          {meta.desc}
                        </p>
                      )}
                      <TicketActions
                        deviceGid={a.device_gid}
                        channelNum={a.channel_num}
                        siteName={a.site_name}
                        label={a.label}
                        ticket={a.ticket}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function HolandaCarritosPage() {
  const { data, isPending, error, isError } = useHolandaOverview();

  const errorMsg = isError
    ? error instanceof ApiError
      ? error.message
      : "Error al cargar el estado de los carritos."
    : null;

  const updatedMin = data?.summary.updated_minutes_ago ?? null;
  const isStale = updatedMin != null && updatedMin > STALE_MIN;
  const agencies = data ? groupAgencies(data.sites) : [];
  const pendientes = data
    ? data.alerts.filter((a) => isUnattended(a.ticket)).length
    : 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Snowflake className="size-5 text-sky-500" />
          <h1 className="text-xl font-semibold">Carritos Holanda</h1>
        </div>
        {updatedMin != null && (
          <span
            className={`text-xs ${isStale ? "font-medium text-amber-600" : "text-muted-foreground"}`}
          >
            Actualizado hace {fmtDur(updatedMin)}
          </span>
        )}
      </div>

      {isStale && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          ⚠️ <b>Datos viejos</b> — el monitor no actualiza hace{" "}
          {fmtDur(updatedMin)} (debería cada ~5 min). Puede estar caído: el
          tablero podría no reflejar el estado real de los carritos.
        </div>
      )}

      {errorMsg && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {errorMsg}
        </div>
      )}

      {isPending && !data && (
        <div className="text-sm text-muted-foreground">Cargando estado…</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Kpi
              label="Activos"
              value={data.summary.active}
              accent="text-green-600"
            />
            <Kpi
              label="Alarmas"
              value={data.summary.alarms}
              accent="text-red-600"
            />
            <Kpi
              label="Avisar guardia"
              value={data.summary.notifying}
              accent="text-orange-600"
            />
            <Kpi
              label="Revisar"
              value={data.summary.warning}
              accent="text-amber-600"
            />
            <Kpi
              label="Posible ruta"
              value={data.summary.possible_route}
              accent="text-sky-600"
            />
            {/* Tomas de dos en las que se apagó UNO y el otro sigue enfriando.
                Aparte de "Alarmas": no es todo apagado, pero hay un carrito
                descongelándose. No cuenta las que siguen en la espera
                (vigilando): ahí puede ser el descanso normal del compresor. */}
            <Kpi
              label="1 de 2 desconectado"
              value={data.summary.parciales ?? 0}
              accent="text-red-600"
            />
            <Kpi
              label="Sin carrito"
              value={data.summary.offline}
              accent="text-muted-foreground"
            />
            {/* Tableros cuyo medidor no transmite. Va aparte de "Sin carrito"
                a propósito: ahí no sabemos si hay carrito o no, y sus tomas NO
                se están vigilando. La unidad es el tablero, que es lo que se
                repara, no el canal. */}
            <Kpi
              label="Tableros sin señal"
              value={data.summary.tableros_sin_senal ?? 0}
              accent="text-purple-600"
            />
          </div>

          {/* Agencias arriba — la vista principal, sin que las alertas la empujen. */}
          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Agencias ({agencies.length}) — clic para ver el detalle
              </h2>
              <Legend />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {agencies.map((a) => (
                <Link
                  key={a.code}
                  to={`/holanda/carritos/${encodeURIComponent(a.code)}`}
                  className="block"
                >
                  <Card className="h-full transition-colors hover:border-primary/50 hover:bg-muted/40">
                    <CardContent className="space-y-2 p-3">
                      <div className="flex items-start justify-between gap-1">
                        <span
                          className="min-w-0 truncate text-sm font-medium"
                          title={a.name}
                        >
                          {a.name}
                        </span>
                        <div className="flex shrink-0 gap-1">
                          {a.alarms > 0 && (
                            <Badge className="bg-red-600 text-white">
                              {a.alarms}
                            </Badge>
                          )}
                          {a.watching > 0 && (
                            <Badge className="bg-amber-400 text-black">
                              {a.watching}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-0.5">
                        {a.sites.flatMap((s) =>
                          s.channels
                            .filter(
                              (ch) =>
                                ch.reading !== "cero" || isAlert(ch.state),
                            )
                            .map((ch) => (
                              <MiniCell
                                key={`${s.device_gid}-${ch.channel_num}`}
                                ch={ch}
                              />
                            )),
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {a.carts} carrito(s) · {a.sites.length} tablero(s)
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>

          {/* Alertas abajo — lista compacta tipo hoja de cálculo, seleccionable. */}
          <section className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Alertas ({data.alerts.length})
              {data.alerts.length > 0 && (
                <span
                  className={pendientes > 0 ? "text-red-600" : "text-green-600"}
                >
                  {" "}
                  · {pendientes} sin atender
                </span>
              )}
            </h2>
            {data.alerts.length === 0 ? (
              <div className="rounded-md border bg-green-500/5 px-4 py-3 text-sm text-green-700 dark:text-green-400">
                Sin alertas — todos los carritos enfriando ✅
              </div>
            ) : (
              <AlertsTable alerts={data.alerts} />
            )}
          </section>
        </>
      )}
    </div>
  );
}
